import {
  Card,
  CardHeader,
  CardBody,
  Label,
  FormGroup,
  Input,
  Container,
  Row,
  Col,
  Button,
} from "reactstrap";
import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import Select from "react-select";
import AdminHeader from "components/Headers/AdminHeader";
import { REGEX, SELECT_CUSTOM_STYLES } from "constants/CommonConstants";
import {
  addInstructor,
  editInstructor,
  getInstructorDetailById,
} from "action/instructor";
import { NotificationContext } from "components/CommonComponents/NotificationProvider";
import { urlDecodedId } from "components/CommonComponents/encodeDecodeFunction";
import { GENDER_OPTIONS } from "components/constant";
import { getSkillList } from "action/skills";
import { useDispatch, useSelector } from "react-redux";

// Static instructor data for prefilling

const initialFields = {
  firstName: {
    value: "",
    error: "",
    isChanged: false,
  },
  lastName: {
    value: "",
    error: "",
    isChanged: false,
  },
  email: {
    value: "",
    error: "",
    isChanged: false,
  },
  mobile: {
    value: "",
    error: "",
    isChanged: false,
  },
  priorExperienceYears: {
    value: "",
    error: "",
    isChanged: false,
  },
  gender: {
    value: "",
    error: "",
    isChanged: false,
  },
  skills: {
    value: [],
    error: "",
    isChanged: false,
  },
  status: {
    value: "ACTIVE",
    error: "",
    isChanged: false,
  },
};

function AddInstructor() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { notify } = useContext(NotificationContext);
  const authData = JSON.parse(localStorage.getItem("p") || "{}");
  const role = authData?.role;
  const navigate = useNavigate();
  const location = useLocation();
  const mode = location.state?.mode || "add";
  const decodedId = id ? urlDecodedId(id) : null;
  const [formData, setFormData] = useState(initialFields);
  const [showLoaderOnClick, setShowLoaderOnClick] = useState(false);
  const [showAlternative, setShowAlternative] = useState({
    loader: false,
    error: "",
  });

  const skillsData = useSelector((state) => state.skills.skillsList);

  useEffect(() => {
    getSkillList(dispatch);
  }, [dispatch]);

  // Prefill form when in edit mode using static data
  useEffect(() => {
    if (mode === "edit") {
      if (!decodedId) {
        notify("danger", "Invalid instructor");
        navigate(role === "SUPER_ADMIN" ? "/super-admin/instructor" : "/admin/instructor");
        return;
      }
      getInstructorDetail(decodedId);
    }
  }, [mode, decodedId]);

  const skillOptions =
    skillsData?.data?.skills
      ?.filter((skill) => skill.status === "ACTIVE")
      .map((skill) => ({
        label: skill.name,
        value: skill.ID,
      })) || [];

  //get the instructor detail by id for edit
  const getInstructorDetail = async (id) => {
    try {
      setShowAlternative({ loader: true, error: "" });
      const res = await getInstructorDetailById(id);
      const instructorDetails = res?.data;

      if (res?.code === 200) {
        setFormData((prev) => ({
          ...prev,
          firstName: {
            value: instructorDetails?.user?.firstName || "",
            error: "",
          },
          lastName: {
            value: instructorDetails?.user?.lastName || "",
            error: "",
          },
          email: { value: instructorDetails?.user?.email || "", error: "" },
          mobile: { value: instructorDetails?.user?.mobile || "", error: "" },
          gender: { value: instructorDetails?.gender || "", error: "" },
          priorExperienceYears: {
            value: instructorDetails?.totalExperienceInMonths,
            error: "",
          },
          skills: {
            value: instructorDetails?.skills?.map((skill) => skill.ID) || [],
            error: "",
            isChanged: false,
          },
          status: {
            value: instructorDetails?.user?.status || "ACTIVE",
            error: "",
            isChanged: false,
          },
        }));
        setShowAlternative({ loader: false, error: "" });
      } else {
        setShowAlternative({ loader: false, error: "" });
        const message = Array.isArray(res?.message)
          ? res.message
              .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
              .join(", ")
          : res?.message || "Failed to load instructor details!";
        notify("danger", message);
      }
    } catch (error) {
      setShowAlternative({ loader: false, error: "" });
    }
  };

  const handleInputChange = (field, value) => {
    if (field === "mobile" || field === "priorExperienceYears") {
      if (!REGEX.ONLY_DIGITS_REGEX.test(value)) {
        return;
      }
    }

    setFormData((prev) => {
      let updatedValue = value;

      if (field === "priorExperienceYears") {
        updatedValue = value === "" ? "" : Number(value);
      }

      return {
        ...prev,
        [field]: {
          ...prev[field],
          value: updatedValue,
          error: "",
          isChanged: true,
        },
      };
    });
  };

  const getValidationError = (name, value) => {
    const isEmpty =
      value === "" ||
      value === null ||
      (Array.isArray(value) && value.length === 0);

    switch (name) {
      case "firstName":
        return isEmpty
          ? "Please enter first name"
          : value.trim().length < 2
            ? "First name must be at least 2 characters"
            : !REGEX.ONLY_ALPHABETS_REGEX.test(value)
              ? "Only alphabets and spaces are allowed"
              : "";

      case "lastName":
        return isEmpty
          ? "Please enter last name"
          : value.trim().length < 2
            ? "Last name must be at least 2 characters"
            : !REGEX.ONLY_ALPHABETS_REGEX.test(value)
              ? "Only alphabets and spaces are allowed"
              : "";

      case "email":
        return isEmpty
          ? "Please enter email"
          : !REGEX.EMAIL_REGEX.test(value)
            ? "Enter a valid email address"
            : "";

      case "mobile":
        return isEmpty
          ? "Please enter a mobile number"
          : !REGEX.PHONE_NO_REGEX.test(value)
            ? "Please enter a valid mobile number"
            : "";

      case "priorExperienceYears":
        return isEmpty
          ? "Please enter years of experience in months"
          : Number(value) < 0
            ? "Experience cannot be negative"
            : "";

      // Uncomment when skill master API is ready
      // case "skills":
      //   return isEmpty
      //     ? "Please select at least one skill"
      //     : "";

      default:
        return "";
    }
  };

  const validateForm = () => {
    let newInstructor = { ...formData };

    //get all the keys in array
    Object.keys(newInstructor).forEach((key) => {
      newInstructor[key] = {
        ...newInstructor[key],
        error: getValidationError(key, newInstructor[key].value),
      };
    });
    setFormData(newInstructor); //if error set error state
    return Object.values(newInstructor).every((item) => item.error === "");
  };

  const handleOnBlur = (name, value) => {
    const trimmed = typeof value === "string" ? value.trim() : value;
    const error = getValidationError(name, trimmed);
    setFormData((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value: trimmed,
        error,
      },
    }));
  };
  //while edit get only edit data
  const getEditedBodyData = (formData) =>
    Object.fromEntries(
      Object.entries(formData) //loop over each child
        .filter(([, field]) => field.isChanged) //remove or filter the unchanges fields
        .map(([key, field]) => {
          if (key === "priorExperienceYears") {
            return [key, Number(field.value)];
          }
          return [key, field.value];
        }),
    );

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setShowLoaderOnClick(true);

      const bodyData =
        mode === "edit"
          ? getEditedBodyData(formData)
          : {
            firstName: formData.firstName.value,
            lastName: formData.lastName.value,
            email: formData.email.value,
            mobile: formData.mobile.value,
            gender: formData.gender.value,
            priorExperienceYears: Number(formData.priorExperienceYears.value),
            skills: formData.skills.value, // [1,2,3]
            status: formData.status.value,
          };

      if (mode === "add") {
        const res = await addInstructor(bodyData);

        if (res?.code === 200) {
          notify("success", res?.message || "Instructor added successfully");
          navigate(role === "SUPER_ADMIN" ? "/super-admin/instructor" : "/admin/instructor");
        } else {
          const message = Array.isArray(res?.message)
            ? res.message
              .map((err) => (err?.Msg ? err.Msg : JSON.stringify(err)))
              .join(", ")
            : res?.message || "Failed to add instructor.";
          notify("danger", message);
        }
      } else {
        if (Object.keys(bodyData).length === 0) {
          notify("info", "No changes detected");
          return;
        }

        const res = await editInstructor(decodedId, bodyData);

        if (res?.code === 200) {
          notify("success", res?.message || "Instructor updated successfully");
          navigate(role === "SUPER_ADMIN" ? "/super-admin/instructor" : "/admin/instructor");
        } else {
          const message = Array.isArray(res?.message)
            ? res.message
              .map((err) => (err?.Msg ? err.Msg : JSON.stringify(err)))
              .join(", ")
            : res?.message || "Failed to edit instructor";
          notify("danger", message);
        }
      }
    } catch (e) {
      notify("danger", "Something went wrong");
    } finally {
      setShowLoaderOnClick(false);
    }
  };

  const handleCancel = () => {
    setFormData(initialFields);
    navigate(role === "SUPER_ADMIN" ? "/super-admin/instructor" : "/admin/instructor");
  };

  return (
    <>
      <AdminHeader parentName="Instructor" />
      <Container className="mt--6" fluid>
        <Row className="justify-content-center">
          <Col>
            <div
              className="d-flex align-items-center cursor text-white"
              onClick={handleCancel}
            >
              <i className="fa-solid fa-angle-left mr-2" />
              <span className="font-weight-bold">Back</span>
            </div>
            <Card className="mb-3 mt-3">
              <CardHeader className="d-flex align-items-center ">
                <i
                // className="fa fa-chevron-left mr-2 cursor"
                // onClick={() =>
                //   navigate(
                //     mode === "edit"
                //       ? role === "SUPER_ADMIN"
                //         ? `/super-admin/instructor/${id}/details`
                //         : `/admin/instructor/${id}/details`
                //       : role === "SUPER_ADMIN"
                //         ? "/super-admin/instructor"
                //         : "/admin/instructor",
                //   )
                // }
                />
                <h3 className="mb-0 section-header-title">
                  {mode === "edit" ? "Edit Instructor" : "Add Instructor"}
                </h3>
              </CardHeader>

              <CardBody>
                {mode === "edit" && showAlternative.loader ? (
                  <div className="loaderContainer">
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </div>
                ) : showAlternative.error ? (
                  <div className="loaderContainer text-muted d-flex flex-column">
                    <h4 className="text-muted">No instructors data available</h4>
                    <span className="text-danger">{showAlternative.error}</span>
                  </div>
                ) : (
                  <>
                    {/* Basic Information */}
                    <Row>
                      <Col md="4">
                        <FormGroup>
                          <Label className="form-control-label">
                            First Name <span className="text-danger"> *</span>
                          </Label>
                          <Input
                            type="text"
                            name="firstName"
                            maxLength={50}
                            value={formData.firstName.value}
                            placeholder="Enter first name"
                            onChange={(e) =>
                              handleInputChange(e.target.name, e.target.value)
                            }
                            onBlur={(e) =>
                              handleOnBlur(e.target.name, e.target.value)
                            }
                            className={
                              formData.firstName.error ? "is-invalid" : ""
                            }
                          />
                          {formData.firstName.error && (
                            <small className="invalid-feedback d-block">
                              {formData.firstName.error}
                            </small>
                          )}
                        </FormGroup>
                      </Col>

                      <Col md="4">
                        <FormGroup>
                          <Label className="form-control-label">
                            Last Name <span className="text-danger"> *</span>
                          </Label>
                          <Input
                            type="text"
                            name="lastName"
                            value={formData.lastName.value}
                            maxLength={50}
                            placeholder="Enter last name"
                            onChange={(e) =>
                              handleInputChange(e.target.name, e.target.value)
                            }
                            onBlur={(e) =>
                              handleOnBlur(e.target.name, e.target.value)
                            }
                            className={
                              formData.lastName.error ? "is-invalid" : ""
                            }
                          />
                          {formData.lastName.error && (
                            <small className="invalid-feedback d-block">
                              {formData.lastName.error}
                            </small>
                          )}
                        </FormGroup>
                      </Col>

                      <Col md="4">
                        <FormGroup>
                          <Label className="form-control-label">
                            Email<span className="text-danger"> *</span>
                          </Label>
                          <Input
                            type="email"
                            name="email"
                            value={formData.email.value}
                            maxLength={100}
                            placeholder="Enter email"
                            onChange={(e) =>
                              handleInputChange(e.target.name, e.target.value)
                            }
                            onBlur={(e) =>
                              handleOnBlur(e.target.name, e.target.value)
                            }
                            className={formData.email.error ? "is-invalid" : ""}
                          />
                          {formData.email.error && (
                            <small className="invalid-feedback d-block">
                              {formData.email.error}
                            </small>
                          )}
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col md="4">
                        <FormGroup>
                          <Label className="form-control-label">
                            Mobile Number{" "}
                            <span className="text-danger"> *</span>
                          </Label>
                          <Input
                            type="tel"
                            name="mobile"
                            value={formData.mobile.value}
                            placeholder="Enter mobile number"
                            maxLength={10}
                            onChange={(e) =>
                              handleInputChange(e.target.name, e.target.value)
                            }
                            onBlur={(e) =>
                              handleOnBlur(e.target.name, e.target.value)
                            }
                            className={
                              formData.mobile.error ? "is-invalid" : ""
                            }
                          />
                          {formData.mobile.error && (
                            <small className="invalid-feedback d-block">
                              {formData.mobile.error}
                            </small>
                          )}
                        </FormGroup>
                      </Col>

                      <Col md="4">
                        <FormGroup>
                          <Label className="form-control-label">Gender</Label>

                          <Select
                            name="gender"
                            options={GENDER_OPTIONS}
                            placeholder="Select gender"
                            styles={SELECT_CUSTOM_STYLES}
                            value={
                              GENDER_OPTIONS.find(
                                (opt) => opt.value === formData.gender.value,
                              ) || null
                            }
                            className={
                              formData.gender.error ? "is-invalid" : ""
                            }
                            onBlur={() =>
                              handleOnBlur("gender", formData.gender.value)
                            }
                            onChange={(selected) =>
                              handleInputChange("gender", selected?.value || "")
                            }
                            isClearable
                          />

                          {formData.gender.error && (
                            <small className="invalid-feedback d-block">
                              {formData.gender.error}
                            </small>
                          )}
                        </FormGroup>
                      </Col>

                      <Col md="4">
                        <FormGroup>
                          <Label className="form-control-label">
                            Prior Years of Experience (in months)
                            <span className="text-danger"> *</span>
                          </Label>
                          <Input
                            type="text"
                            name="priorExperienceYears"
                            value={formData.priorExperienceYears.value}
                            placeholder="Enter year of experience in months eg. 2 "
                            minLength={0}
                            maxLength={3}
                            onChange={(e) =>
                              handleInputChange(e.target.name, e.target.value)
                            }
                            onBlur={(e) =>
                              handleOnBlur(e.target.name, e.target.value)
                            }
                            className={
                              formData.priorExperienceYears.error
                                ? "is-invalid"
                                : ""
                            }
                          />
                          {formData.priorExperienceYears.error && (
                            <small className="invalid-feedback d-block">
                              {formData.priorExperienceYears.error}
                            </small>
                          )}
                        </FormGroup>
                      </Col>
                    </Row>

                    {/* Professional Details */}
                    <Row>
                      <Col md="4">
                        <FormGroup>
                          <Label className="form-control-label">Skills</Label>

                          <Select
                            isMulti
                            options={skillOptions}
                            styles={SELECT_CUSTOM_STYLES}
                            placeholder="Select skills"
                            value={skillOptions.filter((opt) =>
                              formData.skills.value.includes(opt.value),
                            )}
                            className={
                              formData.skills.error ? "is-invalid" : ""
                            }
                            onBlur={(e) =>
                              handleOnBlur(e.target.name, e.target.value)
                            }
                            onChange={(selected) =>
                              handleInputChange(
                                "skills",
                                selected
                                  ? selected.map((item) => item.value)
                                  : [],
                              )
                            }
                          />

                          {formData.skills.error && (
                            <small className="invalid-feedback d-block">
                              {formData.skills.error}
                            </small>
                          )}
                        </FormGroup>
                      </Col>

                      <Col md="4" sm="12">
                        <FormGroup>
                          <Label className="form-control-label">
                            Status <span className="text-danger"> *</span>
                          </Label>

                          <div className="d-flex mt-2">
                            <FormGroup check className="mr-4">
                              <Label check>
                                <Input
                                  type="radio"
                                  name="status"
                                  className="cursor"
                                  value="ACTIVE"
                                  checked={formData.status.value === "ACTIVE"}
                                  onChange={(e) =>
                                    handleInputChange(
                                      e.target.name,
                                      e.target.value,
                                    )
                                  }
                                />
                                Active
                              </Label>
                            </FormGroup>

                            <FormGroup check>
                              <Label check>
                                <Input
                                  type="radio"
                                  name="status"
                                  className="cursor"
                                  value="INACTIVE"
                                  checked={formData.status.value === "INACTIVE"}
                                  onChange={(e) =>
                                    handleInputChange(
                                      e.target.name,
                                      e.target.value,
                                    )
                                  }
                                />
                                Inactive
                              </Label>
                            </FormGroup>
                          </div>
                        </FormGroup>
                      </Col>
                    </Row>
                  </>
                )}

                {/* Action Buttons */}
                <Row className="mt-4">
                  <Col className="text-right">
                    <Button
                      className="btn btn-secondary mr-2 save-btn-sm"
                      onClick={handleCancel}
                    >
                      Cancel
                    </Button>

                    <Button
                      color="default"
                      type="submit"
                      className="btn-deep-blue save-btn-sm"
                      onClick={() => {
                        handleSave();
                      }}
                      disabled={Object.values(formData).some(
                        (item) => item.error !== "",
                      )}
                    >
                      {showLoaderOnClick ? (
                        <i className="fa fa-spinner fa-spin fa-lg text-white" />
                      ) : (
                        "Save"
                      )}
                    </Button>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default AddInstructor;
